// Utility functions for time tracking
// Migrated to support Supabase with localStorage fallback
// Functions remain synchronous for backward compatibility, but sync to Supabase in background

import { supabase } from '../services/supabaseClient';
import { studyTimeService } from '../services/dataService';

const STUDY_MODES = ['flashcard', 'synonym', 'fastattack', 'hardterms', 'quant'] as const;
type StudyMode = typeof STUDY_MODES[number];

const isStudyMode = (value: string): value is StudyMode =>
  STUDY_MODES.includes(value as StudyMode);

const getModeTotalsTemplate = (): Record<StudyMode, number> => ({
  flashcard: 0,
  synonym: 0,
  fastattack: 0,
  hardterms: 0,
  quant: 0
});

let cachedUserId: string | null = null;
let lastUserFetch = 0;
const USER_CACHE_TTL = 60_000; // 1 minute
const HYDRATION_TTL = 5 * 60_000; // 5 minutes

let hydrationPromise: Promise<void> | null = null;
let hydrationUserId: string | null = null;
let hydrationInFlightFor: string | null = null;
let lastHydration = 0;
const remoteTimeCache = new Map<string, number>();

const getCacheKey = (mode: StudyMode, date: string) => `${mode}:${date}`;

// Helper to get current user ID async (cached)
const getCurrentUserIdAsync = async (): Promise<string | null> => {
  const now = Date.now();
  if (now - lastUserFetch < USER_CACHE_TTL && lastUserFetch !== 0) {
    return cachedUserId;
  }

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      cachedUserId = null;
    } else {
      cachedUserId = data.session?.user?.id || null;
    }
  } catch (e) {
    cachedUserId = null;
  } finally {
    lastUserFetch = now;
  }

  return cachedUserId;
};

const hydrateStudyTimeForUser = async (userId: string): Promise<void> => {
  const { data, error } = await studyTimeService.getByUser(userId);
  if (error) {
    throw error;
  }

  const totals = getModeTotalsTemplate();
  remoteTimeCache.clear();

  data?.forEach(record => {
    if (!record?.date) return;
    const rawMode = typeof record.mode === 'string' ? record.mode.toLowerCase() : '';
    if (!isStudyMode(rawMode)) return;
    const mode = rawMode;
    const seconds =
      typeof record.time_seconds === 'number'
        ? record.time_seconds
        : parseInt(String(record.time_seconds || 0), 10) || 0;

    const storageKey = `gre_${mode}_time_today_${record.date}`;
    const localValue = parseInt(localStorage.getItem(storageKey) || '0', 10);
    if (seconds > localValue) {
      localStorage.setItem(storageKey, seconds.toString());
    }

    remoteTimeCache.set(getCacheKey(mode, record.date), seconds);
    totals[mode] += seconds;
  });

  Object.entries(totals).forEach(([mode, total]) => {
    const storageKey = `gre_${mode}_time`;
    const localValue = parseInt(localStorage.getItem(storageKey) || '0', 10);
    if (total > localValue) {
      localStorage.setItem(storageKey, total.toString());
    }
  });

  hydrationUserId = userId;
  lastHydration = Date.now();
};

const hydrateStudyTimeIfNeeded = async (userId: string | null): Promise<void> => {
  if (!userId) {
    hydrationUserId = null;
    hydrationInFlightFor = null;
    remoteTimeCache.clear();
    return;
  }

  if (hydrationUserId === userId && Date.now() - lastHydration < HYDRATION_TTL) {
    return;
  }

  if (hydrationPromise) {
    if (hydrationInFlightFor === userId) {
      return hydrationPromise;
    }
    try {
      await hydrationPromise;
    } catch {
      // swallow; a retry will occur below
    }
    return hydrateStudyTimeIfNeeded(userId);
  }

  hydrationInFlightFor = userId;
  hydrationPromise = hydrateStudyTimeForUser(userId)
    .catch(error => {
      console.warn('Failed to hydrate study time from Supabase', error);
      hydrationUserId = null;
      remoteTimeCache.clear();
      throw error;
    })
    .finally(() => {
      hydrationPromise = null;
      hydrationInFlightFor = null;
    });

  return hydrationPromise;
};

const triggerHydrationCheck = () => {
  getCurrentUserIdAsync()
    .then(userId => hydrateStudyTimeIfNeeded(userId))
    .catch(() => {});
};

// Background sync: Save time to Supabase without blocking
const syncTimeToSupabase = async (
  mode: StudyMode,
  date: string,
  seconds: number
): Promise<void> => {
  const userId = await getCurrentUserIdAsync();
  if (!userId) return;

  await hydrateStudyTimeIfNeeded(userId);

  const cacheKey = getCacheKey(mode, date);
  const previous = remoteTimeCache.get(cacheKey) ?? 0;
  const secondsToSave = Math.max(previous, seconds);

  remoteTimeCache.set(cacheKey, secondsToSave);

  try {
    await studyTimeService.upsert(userId, mode, date, secondsToSave);
  } catch (e) {
    // Silently fail - localStorage is the source of truth
    console.warn(`Background sync to Supabase failed for ${mode} on ${date}:`, e);
  }
};

// Format time helper (unchanged)
export const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Get today's date key (YYYY-MM-DD) - unchanged
export const getTodayKey = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

// Get total time across all modes (synchronous, uses localStorage)
export const getTotalTime = (): number => {
  triggerHydrationCheck();
  try {
    // Return localStorage value immediately (synchronous)
    const flashcardTime = parseInt(localStorage.getItem('gre_flashcard_time') || '0', 10);
    const synonymTime = parseInt(localStorage.getItem('gre_synonym_time') || '0', 10);
    const fastAttackTime = parseInt(localStorage.getItem('gre_fast_attack_time') || '0', 10);
    const hardTermsTime = parseInt(localStorage.getItem('gre_hard_terms_time') || '0', 10);
    const quantTime = parseInt(localStorage.getItem('gre_quant_time') || '0', 10);
    return flashcardTime + synonymTime + fastAttackTime + hardTermsTime + quantTime;
  } catch (e) {
    console.error('Error calculating total time:', e);
    return 0;
  }
};

// Get today's time for a specific mode (synchronous, uses localStorage)
export const getTodayTime = (mode: 'flashcard' | 'synonym' | 'fastattack' | 'hardterms' | 'quant'): number => {
  triggerHydrationCheck();
  try {
    const todayKey = getTodayKey();
    const key = `gre_${mode}_time_today_${todayKey}`;

    const todayTime = parseInt(localStorage.getItem(key) || '0', 10);
    return todayTime;
  } catch (e) {
    console.error(`Error getting today's time for ${mode}:`, e);
    return 0;
  }
};

// Save today's time for a specific mode (synchronous, saves to localStorage)
// Syncs to Supabase in background
export const saveTodayTime = (mode: 'flashcard' | 'synonym' | 'fastattack' | 'hardterms' | 'quant', seconds: number): void => {
  triggerHydrationCheck();
  try {
    const todayKey = getTodayKey();
    const key = `gre_${mode}_time_today_${todayKey}`;
    
    // Save to localStorage immediately (synchronous)
    localStorage.setItem(key, seconds.toString());
    
    // Sync to Supabase in background (non-blocking)
    syncTimeToSupabase(mode, todayKey, seconds).catch(() => {});
  } catch (e) {
    console.error(`Error saving today's time for ${mode}:`, e);
  }
};

// Get total time spent today across all modes (synchronous)
export const getTotalTodayTime = (): number => {
  triggerHydrationCheck();
  try {
    const flashcardToday = getTodayTime('flashcard');
    const synonymToday = getTodayTime('synonym');
    const fastAttackToday = getTodayTime('fastattack');
    const hardTermsToday = getTodayTime('hardterms');
    const quantToday = getTodayTime('quant');
    return flashcardToday + synonymToday + fastAttackToday + hardTermsToday + quantToday;
  } catch (e) {
    console.error('Error calculating total today time:', e);
    return 0;
  }
};

// Get yesterday's date key (YYYY-MM-DD) - unchanged
export const getYesterdayKey = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
};

// Get time for a specific mode on a specific date (synchronous)
export const getTimeForDate = (mode: 'flashcard' | 'synonym' | 'fastattack' | 'hardterms' | 'quant', dateKey: string): number => {
  triggerHydrationCheck();
  try {
    const key = `gre_${mode}_time_today_${dateKey}`;
    const time = parseInt(localStorage.getItem(key) || '0', 10);
    return time;
  } catch (e) {
    console.error(`Error getting time for ${mode} on ${dateKey}:`, e);
    return 0;
  }
};

// Get total time spent yesterday across all modes (synchronous)
export const getTotalYesterdayTime = (): number => {
  triggerHydrationCheck();
  try {
    const yesterdayKey = getYesterdayKey();
    const flashcardYesterday = getTimeForDate('flashcard', yesterdayKey);
    const synonymYesterday = getTimeForDate('synonym', yesterdayKey);
    const fastAttackYesterday = getTimeForDate('fastattack', yesterdayKey);
    const hardTermsYesterday = getTimeForDate('hardterms', yesterdayKey);
    const quantYesterday = getTimeForDate('quant', yesterdayKey);
    return flashcardYesterday + synonymYesterday + fastAttackYesterday + hardTermsYesterday + quantYesterday;
  } catch (e) {
    console.error('Error calculating total yesterday time:', e);
    return 0;
  }
};

// Get total time spent this week (Monday to today, or Sunday to today if today is Sunday) - synchronous
export const getTotalThisWeekTime = (): number => {
  triggerHydrationCheck();
  try {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate start of week (Monday)
    // If today is Sunday (0), start from last Monday (7 days ago)
    // Otherwise, go back to the most recent Monday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - daysToMonday);
    
    let totalWeekTime = 0;
    
    // Sum up time for each day from start of week to today
    for (let i = 0; i <= daysToMonday; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      const flashcardTime = getTimeForDate('flashcard', dateKey);
      const synonymTime = getTimeForDate('synonym', dateKey);
      const fastAttackTime = getTimeForDate('fastattack', dateKey);
      const hardTermsTime = getTimeForDate('hardterms', dateKey);
      const quantTime = getTimeForDate('quant', dateKey);
      
      totalWeekTime += flashcardTime + synonymTime + fastAttackTime + hardTermsTime + quantTime;
    }
    
    return totalWeekTime;
  } catch (e) {
    console.error('Error calculating total this week time:', e);
    return 0;
  }
};

// Legacy function: Save total time for a mode (for backward compatibility)
// Used by components that save cumulative time
export const saveTotalTime = (mode: 'flashcard' | 'synonym' | 'fastattack' | 'hardterms' | 'quant', seconds: number): void => {
  triggerHydrationCheck();
  try {
    // Save to legacy localStorage key for backward compatibility
    const key = `gre_${mode}_time`;
    localStorage.setItem(key, seconds.toString());
    
    // Also save today's time to Supabase in background
    const todayKey = getTodayKey();
    syncTimeToSupabase(mode, todayKey, seconds).catch(() => {});
  } catch (e) {
    console.error(`Error saving total time for ${mode}:`, e);
  }
};

// Legacy function: Get total time for a specific mode (for backward compatibility)
export const getModeTotalTime = (mode: 'flashcard' | 'synonym' | 'fastattack' | 'hardterms' | 'quant'): number => {
  triggerHydrationCheck();
  try {
    return parseInt(localStorage.getItem(`gre_${mode}_time`) || '0', 10);
  } catch (e) {
    console.error(`Error getting total time for ${mode}:`, e);
    return 0;
  }
};
